import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { Video, ResizeMode, AVPlaybackSource } from 'expo-av';
import { useTheme } from '../contexts/ThemeContext';

interface HeroVideoBackgroundProps {
  style?: ViewStyle;
  source: AVPlaybackSource;
}

// Matches theme.ts's bgBase per mode (dark navy / white) — the glossy tint uses the same
// background color as the rest of the app, at a fixed 0.4 alpha, over the video.
const OVERLAY_COLOR: Record<'light' | 'dark', string> = {
  dark: 'rgba(10, 15, 30, 0.4)',
  light: 'rgba(255, 255, 255, 0.4)',
};

/** Full-bleed, looping, muted background video — replaces BrandWireframe's static hero photo
 * on the dual-pane auth screens. No controls, no overlay text: the reel speaks for itself.
 *
 * The source files are portrait (1080x1920, 9:16) and have their own baked-in composed
 * text/illustration — resizeMode 'contain' shows the full frame (small letterbox bars against
 * the black backdrop below) rather than 'cover' cropping that content off the top/bottom to
 * fill the pane's shape.
 * The outer View still clips (overflow: hidden) and gives the video an explicit fill box —
 * expo-av's web shim renders a real <video> tag whose intrinsic file resolution otherwise leaks
 * through position:absolute edges alone, without a concrete width/height to size against. */
export const HeroVideoBackground: React.FC<HeroVideoBackgroundProps> = ({ style, source }) => {
  const { themeMode } = useTheme();
  return (
    // `style` positions/sizes THIS outer box against the caller's pane (e.g. absoluteFill
    // edges). The inner clip View then establishes its own concrete relative box — plain
    // width/height:100%, NOT position:absolute — which is what the <video> element's
    // object-fit:cover (from resizeMode) needs to actually crop against on web. Nesting
    // absolute-in-absolute here was letting the video's real pixel size ignore overflow:hidden
    // and spill past the pane entirely.
    <View style={style} pointerEvents="none">
      <View style={styles.clip} pointerEvents="none">
        <Video
          source={source}
          style={styles.fill}
          videoStyle={styles.fill}
          resizeMode={ResizeMode.STRETCH}
          shouldPlay
          isLooping
          isMuted
          useNativeControls={false}
          pointerEvents="none"
        />
        {/* Glossy tint — same background color as the rest of the app per mode, at 0.4 alpha */}
        <View style={[styles.overlay, { backgroundColor: OVERLAY_COLOR[themeMode] }]} pointerEvents="none" />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  clip: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  fill: {
    width: '100%',
    height: '100%',
  }
});
